import { useAuthState, useLogout } from '@auth/hooks';
import { selectIsAuthenticated, selectIsUserSystemAdmin, clearAuth } from '@auth/state';
import { routes } from '@core/routes';
import { useAppDispatch, useAppSelector } from '@store';
import { useEffect } from 'react';
import { Navigate, Outlet, useLocation, useNavigation } from 'react-router-dom';

import { BaseLayout } from './BaseLayout';
import { Breadcrumbs } from './Breadcrumbs';
import { mainContentWithNavBar, footerWrapper } from './Layout.css';
import { LoadingLayout } from './LoadingLayout';
import { NavigationHeader } from './NavigationHeader';
import { PageFooter } from './PageFooter';
import { StickyHeader } from './StickyHeader';

interface AuthenticatedRouteProps {
  requiresSystemAdmin?: boolean;
}

export function AuthenticatedRoute({ requiresSystemAdmin }: AuthenticatedRouteProps) {
  const { user } = useAuthState();
  const { logout, isLoggingOut } = useLogout();
  const location = useLocation();
  const navigation = useNavigation();
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isUserSystemAdmin = useAppSelector(selectIsUserSystemAdmin);

  // Handle authentication errors - only clear auth if not in logout process
  useEffect(() => {
    if (!isAuthenticated && !isLoggingOut) {
      dispatch(clearAuth());
    }
  }, [isAuthenticated, isLoggingOut, dispatch]);

  // Show loading during logout to prevent any flash of redirect UI
  if (isLoggingOut) {
    return <LoadingLayout />;
  }

  // Show loading during navigation (when loaders are running)
  if (navigation.state === 'loading') {
    return <LoadingLayout />;
  }

  if (!isAuthenticated) {
    // Redirect to login with current location for redirect after login
    return <Navigate replace to={`${routes.login}?redirectTo=${encodeURIComponent(location.pathname + location.search)}`} />;
  }

  // Handle permission errors - redirect to not found page
  if (requiresSystemAdmin && !isUserSystemAdmin) {
    return <Navigate to={routes.notFound} />;
  }

  const handleLogout = () => {
    logout(routes.login); // useLogout hook will handle the logout process
  };

  // Render the authenticated content
  return (
    <BaseLayout
      className={mainContentWithNavBar}
      footer={
        <div className={footerWrapper}>
          <PageFooter />
        </div>
      }
      header={<StickyHeader breadcrumbs={<Breadcrumbs />} navigation={<NavigationHeader user={user ?? undefined} onLogout={handleLogout} />} />}
    >
      <Outlet />
    </BaseLayout>
  );
}
